import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, SelectItem } from 'primeng-lts/api';
import { TranslateService } from '@ngx-translate/core';
import { take, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BaseComponent } from '../../../../components/common/baseComponent/base.component';
import { UIService } from '../../../../ui.service';
import { Configuration } from '../../../../conf/app.constants';
import { WaitLoadingService } from '../../../../components/common/waitLoading/wait-loading.service';
import { PlycaDataService } from '../../../../../services/plyca.service';
import { SignatureService } from '../../../../../services/signature.service';
import { HeaderActionsService } from '../../../../components/common/sub-header/header-actions.service';
import { SignatureProvider, SignatureProviderType, SignatureRequest } from '../../../../../services/signature/signature.objects';
import { DocumentClassDTO, FileDetailsDTO, I18nData, SignatureCommentDTO, SignatureDTO } from '../../../../api/plyca-dtos';
import { isFilePdf } from '../../../../_infrastructure/utils/files/files.utils';
import { flattenSignatures } from '../../../../_infrastructure/utils/signatures/signature.utils';
import { ConnectionSignStoreAdapter } from '../../../../_infrastructure/adapters/store/connection-sign/connection-sign.adapter';
import { DocumentService } from '../../../../documents/document.service';
import { SignatureComment } from '../../../../_core/domain/models/signature/signature-comment.model';

@Component({
  selector: 'signature-tool',
  templateUrl: './signature-tool.page.html',
  styleUrls: ['./signature-tool.page.scss'],
})
export class SignatureToolPage extends BaseComponent implements OnInit, OnDestroy {
  private _isSignModuleAvailable: Map<SignatureProviderType, boolean>;
  private _enabledSignatureProviders: SignatureProvider[];
  private _isShowConfirmation = false;

  public selectedComment: SignatureCommentDTO;
  public signaturesList: SignatureDTO[] = [];
  public documentClass: DocumentClassDTO;
  public signatureToolForm: FormGroup;
  public currentFile: FileDetailsDTO;
  public comments: SelectItem[] = [];

  public isAvailableSign = false;
  public isFileAdded = false;
  public isPdf = false;

  public valueDropdown = '';
  public comment = '';

  constructor(
    private _router: Router,
    private _fb: FormBuilder,
    private _elem: ElementRef,
    private _uiService: UIService,
    private _configuration: Configuration,
    private _routeActivated: ActivatedRoute,
    private _waitService: WaitLoadingService,
    private _plycaService: PlycaDataService,
    private _documentService: DocumentService,
    private _translateService: TranslateService,
    private _signatureService: SignatureService,
    private _headerService: HeaderActionsService,
    private _confirmationService: ConfirmationService,
    private readonly _connectionSignStoreAdapter: ConnectionSignStoreAdapter
  ) {
    super();
  }

  ngOnInit(): void {
    this._loadDataResolver();
    this._loadHeaderActionsInit();
    this._getConfigurationFile();
    this._controlWaitingScreen();
    this._createFormComments();
  }

  private _loadDataResolver(): void {
    const data = this._routeActivated.snapshot.data['signature'];
    this._enabledSignatureProviders = data.providers;
    this._isSignModuleAvailable = data.availability;
  }

  private _loadHeaderActionsInit(): void {
    this._headerService.setTaskDescription2(this._elem.nativeElement, 'ControlPanel.signatureTool.title', 'fas fa-pen-nib', true, false);
    this._backButtonHeader();
  }

  private _controlWaitingScreen(): void {
    this._waitService.startWaiting(this._translateService.instant('Signature.AutoFirma.Connecting'));
    this.subscribe(this._connectionSignStoreAdapter.selectConnectionStatus(), (status: boolean) => {
      if (status) {
        this._waitService.cancelWaiting();
        this.isAvailableSign = true;
        //TODO: in v18...includes(RoutingPaths.SignatureTool)
      } else if (status === false && this._router.url.includes('signatureTool')) {
        this.subscribe(this._uiService.showMessage(this._translateService.instant('Signature.AutoFirma.Error.Connection')).onClose,
          () => this._uiService.routeBack());
      }
    })
  }

  private _getConfigurationFile(): void {
    this.subscribe(this._plycaService.get<DocumentClassDTO>(this._configuration.DOCUMENTCLASS_SERVICE_URL, 'OTR',
      { 'expand': 'configuration' }), (documentClass: DocumentClassDTO) => {
        this.documentClass = documentClass
      })
  }

  private _createFormComments(): void {
    this.signatureToolForm = this._fb.group({
      selectedComment: null,
      comment: ''
    });
    this._syncDropdownWithTextarea();
  }

  private _syncDropdownWithTextarea(): void {
    let isFirstChange = true;
    this._isShowConfirmation = true;
    this.signatureToolForm.controls['selectedComment']?.valueChanges.subscribe((selectedValue) => {
      const commentControl = this.signatureToolForm.controls['comment'];
      this.valueDropdown = selectedValue;

      if (this._handleFirstChange(selectedValue, commentControl, { isFirstChange })) {
        return;
      }
      if (this._isShowConfirmation) {
        this._showConfirmation(selectedValue, commentControl);
      }
    });
  }

  private _handleFirstChange(selectedValue: SignatureComment, commentControl: AbstractControl, isFirstChangeRef: { isFirstChange: boolean }): boolean {
    if (isFirstChangeRef.isFirstChange) {
      if (!commentControl.value) {
        commentControl.setValue(selectedValue);
        isFirstChangeRef.isFirstChange = false;
        return true;
      }
      isFirstChangeRef.isFirstChange = false;
    }
    return false;
  }

  private _showConfirmation(selectedValue: string, commentControl: AbstractControl): void {
    const confirmSelection = new Subject<boolean>();
    this._confirmationService.confirm({
      message: this._translateService.instant('SignatureTool.discardChanges'),
      acceptLabel: this._translateService.instant('General.discard.accept'),
      rejectLabel: this._translateService.instant('General.discard.cancel'),
      accept: () => {
        commentControl.setValue(selectedValue);
        confirmSelection.next(true);
      },
      reject: () => {
        confirmSelection.next(false);
      },
    });
  }

  private _loadHeaderWithDocument(): void {
    this._headerService.clearActions();
    this._uploadFileButtonHeader();
    this._signButtonHeader();
  }

  private _loadBackButtonWithActions(): void {
    this._headerService.addWindowDivider(null);
    this._backButtonHeader();
  }

  private _loadHeaderActionsSign(): void {
    this._loadHeaderWithDocument()
    this._loadBackButtonWithActions();
  }

  private _loadHeaderActionsSave(): void {
    this._loadHeaderWithDocument();
    this._saveButtonHeader();
    this._loadBackButtonWithActions();
  }

  private _backButtonHeader(): void {
    this.subscribe(this._headerService.addAction(
      'General.back', 'fas fa-chevron-left', 'btn-outline-secondary', true),
      () => { this._uiService.routeBack(); });
  }

  private _signButtonHeader(): void {
    const provider = this._enabledSignatureProviders[0];
    this.subscribe(this._headerService.addAction(
      'SignatureTool.sign', provider.config?.btnIcon, provider.config?.btnClass,
      () => true,
      () => this._isSignModuleAvailable && this.isAvailableSign,
      'Signature.AutoFirma.ModuleNotLoaded'
    ).pipe(tap(() => this._signDocument(provider.type))
    ));
  }

  private _uploadFileButtonHeader(): void {
    this.subscribe(this._headerService.addAction(
      'SignatureTool.anotherDocument', 'fa-sync-alt fas', 'btn-warning',
      () => true,
    ).pipe(tap(() => {
      this._headerService.clearActions();
      this._backButtonHeader();
      this.isFileAdded = false;
      this.currentFile = undefined;
    })
    ));
  }

  private _saveButtonHeader(): void {
    this.subscribe(this._headerService.addAction(
      'SignatureTool.download', 'fa-download fas', 'btn-primary',
      () => true,
    ).pipe(tap(() => {
      this._downloadFile(this.currentFile);
    })
    ));
  }

  private _signDocument(type: SignatureProviderType) {
    if ([this.currentFile].length > 0) {
      this._waitService.startWaiting();
      [this.currentFile].forEach((fileSign: FileDetailsDTO) => {
        const request = new SignatureRequest();
        request.file = fileSign;
        request.documentClass = this.documentClass;
        request.comment = this.signatureToolForm.controls['comment'].value;
        request.provider = type;
        this._getSignatureService(request);
      });
    }
  }

  private _getSignatureService(request: SignatureRequest): void {
    this._signatureService.sign(request).pipe(take(1)).subscribe((request: SignatureRequest) => {
      if (request) {
        this._responseSignatures(request);
      } else {
        this._waitService.startWaiting(this._translateService.instant('Signature.AutoFirma.ModuleNotLoaded'));
      }
    });
  }

  private _responseSignatures(request: SignatureRequest): void {
    this._waitService.cancelWaiting();
    this._loadSignatures(request.file);
    this._downloadFile(request.file);
    this.currentFile = request.file;
    this._isShowConfirmation = false;
    this.signatureToolForm.controls['selectedComment'].setValue(null);
    this.signatureToolForm.controls['comment'].reset();
    this._isShowConfirmation = true;
    this._loadHeaderActionsSave();
  }

  public addFile(file: FileDetailsDTO): void {
    this.currentFile = file;
    this.isFileAdded = true;
    this.isPdf = isFilePdf(file);

    if (this.isPdf) {
      this._loadHeaderActionsSign();
      this._loadSignatures(file);
    } else {
      this._uiService.showMessage(new I18nData('SignatureTool.errorFormat'));
    }
  }

  private _loadSignatures(file: FileDetailsDTO): void {
    this.signaturesList = flattenSignatures(file.signatures);
  }

  private _downloadFile(file: FileDetailsDTO): void {
    const fileFiltered = {
      ...file,
      name: `PDF001`
    }
    this._documentService.downloadEmbedded(fileFiltered);
  }

  ngOnDestroy(): void {
    this.currentFile = undefined;
    super.ngOnDestroy();
  }
}